/**
 * The opportunity list — PRD §2.
 *
 * "Show five ranked cards. A card contains a headline, a short editorial
 * rationale, and a single action to get research for a draft." And, explicitly:
 * "Do not show source outlets, scores, generic 'why it fits your wishes'
 * labels, multiple controls, or a company field."
 *
 * The rows are ruled rather than boxed — brand book §8, "story list: thin
 * separators, no heavy cards".
 */

import React from 'react';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import { copyFor, SHARED } from './audienceCopy';
import { LandingCapabilities } from './LandingStep';

export default function ListStep({
  audience, opportunities, onRequestReport, onRevise, busyStoryId, error, plain = false,
}) {
  const copy = copyFor(audience);

  if (opportunities.length === 0) {
    return (
      <>
        <p className="cmp-eyebrow">Your first list</p>
        <h1 className="cmp-title">{copy.listTitle}</h1>
        <div className="cmp-notice">{SHARED.firstListEmpty}</div>
        <div className="cmp-actions">
          <button type="button" className="cmp-btn cmp-btn--primary" onClick={onRevise}>
            {SHARED.reviseBrief}
          </button>
        </div>
      </>
    );
  }

  return (
    <>
      <p className="cmp-eyebrow">Your first list</p>
      <h1 className="cmp-title">{copy.listTitle}</h1>
      <p className="cmp-lede">{copy.listSupport}</p>

      {/* A refused report request has to say so here. The list is the only
          screen the visitor is on when they ask, so without this the button
          simply does nothing and they click it again. */}
      {error && <div className="cmp-notice">{error}</div>}

      <div className="cmp-list">
        {opportunities.map((item) => (
          <article className="cmp-opportunity" key={item.storyId}>
            <div className="cmp-opportunity-index">
              {String(item.position).padStart(2, '0')}
            </div>
            <div>
              <h2 className="cmp-opportunity-headline">{item.headline}</h2>
              {/* Absent rather than filled with a generic line when the run
                  produced nothing showable. §2 bans "why it fits your wishes"
                  labels, and a placeholder reason is exactly that. */}
              {item.reason && <p className="cmp-opportunity-reason">{item.reason}</p>}
              {/* How much reporting sits behind the opportunity. §2 rules out
                  source outlets and scores; a count is neither, and without it
                  the list asserts relevance while showing none of its working.
                  Omitted rather than shown as "0" when the run did not
                  record one. */}
              {item.articleCount > 0 && (
                <p className="cmp-opportunity-meta">
                  <ArticleOutlinedIcon fontSize="inherit" aria-hidden />
                  {item.articleCount === 1 ? '1 article' : `${item.articleCount} articles`}
                </p>
              )}
            </div>
            {/* A single action. No share, no save, no preview — §2 rules out
                multiple controls, and every extra one is a way to not choose. */}
            <div className="cmp-opportunity-action">
              {/* The card already running stays clickable — see MockList. */}
              <button
                type="button"
                className="cmp-btn cmp-btn--secondary"
                onClick={() => onRequestReport(item)}
                disabled={Boolean(busyStoryId) && busyStoryId !== item.storyId}
                aria-label={busyStoryId === item.storyId
                  ? `Open the research being prepared for ${item.headline}`
                  : undefined}
              >
                {busyStoryId === item.storyId ? SHARED.cardPreparing : copy.cardAction}
              </button>
            </div>
          </article>
        ))}
      </div>

      {/* A list can legitimately come back with two rows, and the screen then
          ended in half a viewport of empty canvas. Same approved copy as the
          landing page and the report wait; no action of its own, so it cannot
          compete with the one action §2 allows per card. */}
      <LandingCapabilities audience={audience} plain={plain} />
    </>
  );
}
